import {
  GetPlanDocument,
  GetPlanQuery,
  PlanTableDocument,
  PlanTableQuery,
  SavePlan_Input,
  SavePlanMutation,
  useGetPlanQuery,
  useSavePlanMutation,
  GetInstallationPlansQuery,
  GetInstallationPlansDocument
} from 'generated/graphql';
import _ from 'lodash';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';


export type IFormState = {
  id: string;
  installationId: string;
  name: string;
  planSetId: string;
  planVersionId: string;
  planVersionName: string;
  projectId: string;
};

const initialState: IFormState = {
  id: '',
  installationId: '',
  name: '',
  planSetId: '',
  planVersionId: '',
  planVersionName: '',
  projectId: ''
};

export function useFormState(id: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Id',
        type: 'text'
      },
      installationId: {
        label: 'Installation',
        type: 'text'
      },
      name: {
        label: 'Name',
        type: 'text'
      },
      planSetId: {
        label: 'Plan Set',
        type: 'text',
        required: true
      },
      planVersionId: {
        label: 'Plan Version',
        type: 'text'
      },
      planVersionName: {
        label: 'Plan Version Name',
        type: 'text'
      },
      projectId: {
        label: 'Project',
        type: 'text'
      }
    }
  });

  const { data, loading, called } = useQuery(id);

  useEffect(() => {
    if (!loading && formState.id === '' && called) {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
  }, [
    data,
    loading,
    called,
    formState,
    dispatch
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}



export interface IData {
  id: string;
  name: string;
  planSetId: string;
}

export function useQuery(id: string) {
  const tuple = useGetPlanQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const plan = tuple.data?.plan;
      const data: IData = {
        id: plan?.id ?? '',
        name: plan?.name ?? '',
        planSetId: plan?.planSet.id ?? ''
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSavePlanForm() {
  const [
    fnMutate,
    tuple
  ] = useSavePlanMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SavePlan_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<GetPlanQuery, SavePlanMutation>(
            GetPlanDocument, (prevState, { mutationResult }) => {
              if (mutationResult.data?.savePlan) {
                return {
                  ...prevState,
                  plan: {
                    ...prevState.plan,
                    ...mutationResult.data?.savePlan
                  }
                };
              }
              return prevState;
            }
          ),

          ...gqlCacheUtil.genUpdateQueryMap<GetInstallationPlansQuery, SavePlanMutation>(
            GetInstallationPlansDocument, (prevState, { mutationResult, queryVariables }) => {
              if (!queryVariables.installationId || !prevState.installation?.id) {
                return prevState;
              }
              if (!mutationResult.data?.savePlan?.id) {
                return prevState;
              }
              const existingPlan = prevState.installation.plan?.id;


              if (prevState.installation?.id === queryVariables.installationId) {
                return {
                  ...prevState,
                  installation: {
                    ...prevState.installation,
                    plan: existingPlan
                      ? {
                        ...prevState.installation.plan,
                        ...mutationResult.data?.savePlan
                      }
                      : {
                        ...mutationResult.data?.savePlan
                      }
                  }
                };
              }
              return prevState;

            }
          ),
          ...gqlCacheUtil.genUpdateQueryMap<PlanTableQuery, SavePlanMutation>(
            PlanTableDocument, (prevState, { mutationResult, queryVariables }) => {
              if (!mutationResult.data?.savePlan) {
                return prevState;
              }
              const existingPlan = queryVariables?.id;
              let tempPlans;
              if (existingPlan) {
                tempPlans = prevState.plans.map((node) => {
                  const isMatch = node.id === mutationResult.data?.savePlan?.id;
                  if (isMatch) {
                    return {
                      ...node,
                      ...mutationResult.data?.savePlan
                    };
                  }
                  return node;
                });
              } else {
                tempPlans = _.compact([
                  ...prevState.plans,
                  mutationResult.data.savePlan
                ]);
              }
              return {
                ...prevState,
                plans: tempPlans
              };
            }
          )
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
